/* eslint-disable react/no-danger */
import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from 'react';
import {
  EVCard, EVTypography, EVPaper, EVBox, EVLink, EVSnackbar, EVAlert,
} from '@eagleview/ev-comp-library';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'utils/action.util';
import {
  EVONE_ACCESS_INGREDIENT,
  ACCOUNT_ONBOARDING_ACTIVATE_URL,
  EVONE_APP_CODE,
  MY_EAGLEVIEW_URL,
  WAIT_AFTER_REVOKE_TOKEN_API_CALL,
} from 'constants/AppConstants';
import Shimmer from 'commonComponents/Loader/Shimmer.component';
import * as sideBarActions from 'features/side-bar/SideBar.actions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import { signOutUserWithOnboardingDetails, wait, buildEvoneOauthLoginUrl } from 'utils/app.utils';
import * as notificationAction from 'features/NotificationsPopup/Notifications.action';
import * as actions from './UserEntitledApps.actions';
import selector from './UserEntitledApps.selector';
import useStyles from './UserEntitledApps.style';

/**
 * UserEntitledApps Component - Renders user entitled app list screen
 */
const UserEntitledApps = (props) => {
  const {
    t, thirdPartyAppList, userDetails, showToast, errorToastMessage, thirdPartyAppsFetched, userDetailsFetched,
    actions: {
      fetchUserEntitledApps, closeErrorToast, updateUserOnboardStatus, getUserDetails,
    },
  } = props;
  const classes = useStyles();
  const flags = useFlags();
  const dispatch = useDispatch();
  const history = useHistory();

  const [inProgress, setInProgress] = useState(true);

  // #genai Activation handoff guard: once a /start?source=actv redirect (devportal callback,
  // provisional sign-out, account-onboarding URL) has been initiated from handleUserDetailsFetched,
  // suppress competing internal redirects (first-entitled-app, EVONE oauth, empty-list `/`) so the
  // hand-off cannot be cancelled by entitled-apps state changes landing in the same render commit.
  const activationHandoffInProgressRef = useRef(false);

  const alertMsg = `${t(errorToastMessage)}`;
  useEffect(() => {
    // #genai Skip the `/` fallback while an activation hand-off is in flight; otherwise it
    // would cancel a pending window.location.replace to onboardingAppLink / sign-out endpoint.
    if (activationHandoffInProgressRef.current) {
      return;
    }
    // If not in progress and no apps, redirect to root for consistent error handling
    if (!inProgress && thirdPartyAppList.length === 0) {
      window.location.replace('/');
    }
  }, [inProgress, thirdPartyAppList]);

  const displayedApps = useMemo(() => {
    const nextApps = thirdPartyAppList.length > 1
      ? thirdPartyAppList
      : [];
    return nextApps;
  }, [thirdPartyAppList]);

  const handleAppLinkClick = useCallback((url = '') => window.location.replace(url), []);

  const componentDidMount = useCallback(() => {
    getUserDetails(true);
  }, []);

  useEffect(componentDidMount, []);

  const getQueryParam = (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };

  const revokeAccessTokenAndSignOut = async (onboardingDetails) => {
    // Revoke access token first
    dispatch(notificationAction.revokeAccessToken());

    // Wait for the revoke token API call to complete
    await wait(WAIT_AFTER_REVOKE_TOKEN_API_CALL);

    // Proceed with logout
    signOutUserWithOnboardingDetails(onboardingDetails);
  };

  const handleUserDetailsFetched = useCallback(() => {
    if (userDetailsFetched) {
      const sourceParam = getQueryParam('source');
      const autoRedirectionEnabled = get(flags, 'autoRedirection', false);
      const fromActivationSignoutEnabled = get(flags, 'fromActivationSignout', false);

      if (userDetails?.onboardStatus === 'complete') {
        if (fromActivationSignoutEnabled && sourceParam === 'actv' && userDetails.organization.isOrgProvisional) {
          const onboardingClientId = userDetails?.authState;
          const onboardingAppLink = `${userDetails?.onboardingAppLink}/authorize/callback`;

          // Prepare onboarding details to be stored
          const onboardingDetails = {
            clientId: onboardingClientId,
            onboardingAppLink,
            onboardingAppName: userDetails?.onboardingAppName,
            onboardingSourceId: userDetails?.onboardingSourceId,
            fromActivation: true,
            is3legged: 'false',
            stateData: userDetails?.state || '/',
          };

          activationHandoffInProgressRef.current = true;
          setTimeout(() => {
            revokeAccessTokenAndSignOut(onboardingDetails);
          }, 100);
          return;
        }

        if (autoRedirectionEnabled && sourceParam === 'actv') {
          if (userDetails?.onboardingAppLink) {
            const onboardingAppLink = `${userDetails.onboardingAppLink}/authorize/callback`;
            activationHandoffInProgressRef.current = true;
            window.location.replace(onboardingAppLink);
            return;
          }
        }
        fetchUserEntitledApps();
      } else if (userDetails?.onboardStatus !== 'complete' && userDetails?.authState?.length && userDetails?.appURL?.length) {
        updateUserOnboardStatus();
        if (fromActivationSignoutEnabled && sourceParam === 'actv' && userDetails.organization.isOrgProvisional) {
          // Store partner info in state parameter instead of URL query params
          const stateData = '/';
          const onboardingDetails = {
            clientId: userDetails?.authState,
            onboardingAppLink: userDetails?.appURL,
            onboardingAppName: userDetails?.appName,
            onboardingSourceId: userDetails?.onboardingSourceId,
            fromActivation: true,
            is3legged: 'true',
            stateData,
            partnerName: userDetails?.appName,
            partnerUrl: userDetails?.appURL,
          };
          activationHandoffInProgressRef.current = true;
          setTimeout(() => {
            revokeAccessTokenAndSignOut(onboardingDetails);
          }, 100);
          return;
        }
        if (sourceParam === 'actv') {
          activationHandoffInProgressRef.current = true;
        }
        window.location.replace(`${ACCOUNT_ONBOARDING_ACTIVATE_URL}?partner_name=${userDetails?.appName}&partner_url=${userDetails?.appURL}`);
      } else if (userDetails?.userName?.length === 0) {
        window.location.replace(MY_EAGLEVIEW_URL);
      }
    }
  }, [userDetails, userDetailsFetched]);

  useEffect(handleUserDetailsFetched, [userDetailsFetched]);

  const handleAppsFetched = useCallback(() => {
    if (thirdPartyAppsFetched && userDetails?.userName?.length > 0) {
      // #genai If an activation hand-off (devportal callback / provisional sign-out /
      // account-onboarding URL) is already in flight, do not let a competing first-entitled-app
      // or EVONE redirect cancel it. Bailing also leaves inProgress=true so the spinner stays.
      if (activationHandoffInProgressRef.current) {
        return;
      }
      const firstApp = thirdPartyAppList[0];
      // #genai EVONE: require EVONE_ACCESS ingredient before external redirect; else SPA home.
      if (firstApp?.appCode === EVONE_APP_CODE) {
        const evoneIngredients = firstApp?.entitlements?.[EVONE_APP_CODE]?.ingredients || [];
        if (evoneIngredients.includes(EVONE_ACCESS_INGREDIENT)) {
          // #genai EVONE: hand off via auth-service /oauth2/login (shared with Horizon banner click)
          const evoneLoginUrl = buildEvoneOauthLoginUrl(thirdPartyAppList, userDetails?.userName);
          if (evoneLoginUrl) {
            window.location.replace(evoneLoginUrl);
            return;
          }
        }
        history.replace('/');
        return;
      }
      if (firstApp?.appURL) {
        window.location.replace(firstApp.appURL);
        return;
      }
      setInProgress(false);
    }
  }, [thirdPartyAppsFetched, thirdPartyAppList, userDetails?.userName]);
  useEffect(handleAppsFetched, [handleAppsFetched]);

  const handleSnackbarClose = () => closeErrorToast();

  return (
    <>
      { !inProgress ? (
        <EVBox>
          <EVBox className={classes.box}>
            <EVCard className={classes.card}>
              <EVBox className={classes.portalBox}>
                <EVTypography variant="subtitle1" gutterBottom className={classes.portalHeader}>
                  {t('YourPortals')}
                </EVTypography>
              </EVBox>

              <EVBox className={classes.appBox}>
                {
                  displayedApps.map(({
                    appName,
                    appURL,
                    appLink,
                  }) => (
                    <EVPaper className={classes.appPaper} elevation={3} key={appName}>
                      <EVTypography noWrap variant="h2" gutterBottom className={classes.appTypo}>
                        {appName}
                      </EVTypography>
                      <EVLink
                        // eslint-disable-next-line
                        onClick={handleAppLinkClick.bind(this, appURL)}
                        className={classes.linkStyle}
                      >
                        <EVTypography variant="subtitle1" className={classes.appUrlTypo}>{appLink}</EVTypography>
                      </EVLink>
                    </EVPaper>
                  ))
                }
              </EVBox>
            </EVCard>
          </EVBox>
          <EVSnackbar
            open={showToast}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={10000}
            onClose={handleSnackbarClose}
            style={{ top: '50px' }}
          >
            <EVAlert severity="error">
              {alertMsg}
            </EVAlert>
          </EVSnackbar>
        </EVBox>
      ) : (
        <div>
          <Shimmer appName="" />
        </div>
      )}
    </>
  );
};

UserEntitledApps.propTypes = {
  /**
     * @property {Function} t - A method which is triggered for translation.
     */
  t: PropTypes.func.isRequired,
  /**
   * @property {Object} actions - Object containing the list of actions that are dispatched by the component.
   */
  actions: PropTypes.shape({
    fetchUserEntitledApps: PropTypes.func,
    getUserDetails: PropTypes.func,
    closeErrorToast: PropTypes.func,
    updateUserOnboardStatus: PropTypes.func,
  }).isRequired,
  /**
     * @property {Array} thirdPartyAppList - Entitled third-party apps from API.
     */
  thirdPartyAppList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
     * @property {Object} userDetails - User details.
     */
  userDetails: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    onboardStatus: PropTypes.string,
    authState: PropTypes.string,
    appURL: PropTypes.string,
    appName: PropTypes.string,
    onboardingSourceId: PropTypes.string,
    onboardingAppName: PropTypes.string,
    onboardingAppLink: PropTypes.string,
    organization: PropTypes.shape({
      isOrgProvisional: PropTypes.bool,
    }),
    state: PropTypes.string,
  }),
  /**
   * @property {Bool} showToast - boolean flag to show toast message.
   */
  showToast: PropTypes.bool.isRequired,
  /**
     * @property {String} errorToastMessage - error message to be displayed in toast.
     */
  errorToastMessage: PropTypes.string.isRequired,
  /**
   * @property {Bool} thirdPartyAppsFetched - Indicates third-party app list fetch completed (success or error path).
   */
  thirdPartyAppsFetched: PropTypes.bool.isRequired,
  /**
   * @property {Bool} userDetailsFetched - boolean flag to indicate successfully fetched user details.
   */
  userDetailsFetched: PropTypes.bool.isRequired,
};

UserEntitledApps.defaultProps = {
  userDetails: PropTypes.shape({
    onboardStatus: '',
    appURL: '',
    authState: '',
    appName: '',
    userName: '',
    firstName: '',
    lastName: '',
    onboardingSourceId: '',
    onboardingAppName: '',
    onboardingAppLink: '',
    organization: PropTypes.shape({
      isOrgProvisional: false,
    }),
    state: '',
  }),
};

export default compose(withTranslation(), connect(selector, bindActionCreators({ ...actions, ...sideBarActions })))(UserEntitledApps);
